import type { Me, PartnerSummary } from '@trendator/shared';
import { useEffect, useState } from 'react';
import { ApiError, api } from '../api';
import { ChevronDim, ClipIcon, CopyIcon, DownArrowIcon, Headset, LinkIcon } from '../components/icons';
import { rub } from '../format';
import { copyText, haptic, hapticResult, openLegal, openTelegram } from '../telegram';
import { Loading } from './Status';
import { ReportSheet } from '../components/ReportSheet';

export const SUPPORT_URL = (import.meta.env.VITE_SUPPORT_URL as string | undefined) || null;

/** Профиль — docs/design/Profile.dc.html (Партнёрка) и Settings.dc.html (Аккаунт). */
export function Profile({
  me,
  onMe,
  onWithdraw,
  onTwin,
}: {
  me: Me;
  onMe: (me: Me) => void;
  onWithdraw: () => void;
  onTwin: () => void;
}) {
  const [tab, setTab] = useState<'partner' | 'account'>('partner');
  return (
    <div className="profile">
      <header className="profile__header">
        <span className="profile__avatar" />
        <span className="profile__who">
          <span className="profile__name">{me.firstName}</span>
          <span className="profile__balance">Баланс {rub(me.balance)}</span>
        </span>
        {SUPPORT_URL ? (
          <button type="button" className="glass icon-btn" aria-label="Поддержка" onClick={() => openTelegram(SUPPORT_URL)}>
            <Headset />
          </button>
        ) : null}
      </header>

      <div role="tablist" aria-label="Разделы профиля" className="tabs3 tabs3--2">
        {(
          [
            ['partner', 'Партнёрка'],
            ['account', 'Аккаунт'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            onClick={() => {
              haptic('select');
              setTab(id);
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'partner' ? <Partner onWithdraw={onWithdraw} /> : <Account me={me} onMe={onMe} onTwin={onTwin} />}
    </div>
  );
}

function Partner({ onWithdraw }: { onWithdraw: () => void }) {
  const [p, setP] = useState<PartnerSummary | null>(null);
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState(false);
  const [postbackOpen, setPostbackOpen] = useState(false);

  useEffect(() => {
    api
      .partner()
      .then(setP)
      .catch(() => setError(true));
  }, []);

  if (error) return <p className="error-text profile__msg">Не получилось загрузить партнёрку. Проверь интернет.</p>;
  if (!p) return <Loading inline />;
  const link = p.appLink ?? p.link;

  return (
    <>
      <section className="glass profile__hero">
        <span className="profile__pct">{p.terms.firstPercent}%</span>
        <span className="profile__hero-title">с первой покупки каждого друга</span>
        <span className="profile__hero-sub">и {p.terms.nextPercent}% со всех следующих, пока он с нами</span>
      </section>

      <div className="glass profile__link">
        <label htmlFor="reflink" className="sr-only">
          Твоя ссылка
        </label>
        <input id="reflink" type="text" readOnly value={link.replace(/^https:\/\//, '')} onFocus={(e) => e.target.select()} />
        <button
          type="button"
          className="profile__copy"
          onClick={async () => {
            haptic();
            if (await copyText(link)) {
              setCopied(true);
              setTimeout(() => setCopied(false), 1800);
            }
          }}
        >
          <CopyIcon />
          {copied ? 'Скопировано' : 'Копировать'}
        </button>
      </div>

      <div className="profile__stats">
        <div className="profile__stat">
          <b>{p.invited}</b>
          <span>пришло</span>
        </div>
        <div className="profile__stat">
          <b>{p.bought}</b>
          <span>купили</span>
        </div>
        <div className="profile__stat">
          <b className="profile__stat-money">{rub(p.available)}</b>
          <span>к выводу</span>
        </div>
      </div>
      {p.held > 0 ? (
        <p className="profile__hold">
          Ещё {rub(p.held)} в холде {p.terms.holdDays} дней на случай возврата
          {p.nextReleaseAt ? ` · ближайшие освободятся ${new Date(p.nextReleaseAt).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}` : ''}
        </p>
      ) : null}

      <div className="glass-list profile__list">
        <button type="button" className="glass-list__row glass-list__row--btn" onClick={onWithdraw}>
          <span className="glass-list__icon" style={{ background: '#34C759' }}>
            <DownArrowIcon />
          </span>
          <span className="trend__face-text">
            <span className="trend__face-name">Вывод</span>
            <span className="trend__face-sub">
              {p.pendingPayout ? `Заявка на ${rub(p.pendingPayout.amount)} в обработке` : 'СБП или USDT раз в неделю, на баланс — сразу'}
            </span>
          </span>
          <ChevronDim />
        </button>
        <div className="glass-list__sep" />
        <div className="glass-list__row profile__row-off">
          <span className="glass-list__icon" style={{ background: '#AF52DE' }}>
            <ClipIcon />
          </span>
          <span className="trend__face-text">
            <span className="trend__face-name">Готовые креативы</span>
            <span className="trend__face-sub">Ролики со ссылкой под тебя · скоро</span>
          </span>
        </div>
        <div className="glass-list__sep" />
        <button type="button" className="glass-list__row glass-list__row--btn" onClick={() => setPostbackOpen((o) => !o)} aria-expanded={postbackOpen}>
          <span className="glass-list__icon" style={{ background: '#0A84FF' }}>
            <LinkIcon />
          </span>
          <span className="trend__face-text">
            <span className="trend__face-name">Саб-айди и постбэк</span>
            <span className="trend__face-sub">{p.postbackUrl ? 'Постбэк настроен' : 'Для арбитража: ссылка с ?sub и S2S'}</span>
          </span>
          <ChevronDim />
        </button>
        {postbackOpen ? <Postback p={p} onSaved={setP} /> : null}
      </div>
    </>
  );
}

function Postback({ p, onSaved }: { p: PartnerSummary; onSaved: (p: PartnerSummary) => void }) {
  const [url, setUrl] = useState(p.postbackUrl ?? '');
  const [state, setState] = useState<'idle' | 'busy' | 'saved' | 'error'>('idle');
  const base = p.appLink ?? p.link;
  const save = async (value: string | null) => {
    setState('busy');
    try {
      onSaved(await api.setPostback(value));
      setState('saved');
      hapticResult('success');
    } catch {
      setState('error');
    }
  };
  return (
    <div className="profile__postback">
      <p>
        Саб-айди — в конце ссылки: <code>{base}_sub1</code> (латиница, цифры, _ и -, до 40 символов).
      </p>
      <p>
        Постбэк — GET на твой https-адрес при событиях <code>reg</code>, <code>ftd</code> (первая оплата), <code>dep</code>. Макросы:{' '}
        <code>{'{event} {sub_id} {amount} {payout} {user_id}'}</code>
      </p>
      <input
        aria-label="Адрес постбэка"
        value={url}
        placeholder="https://tracker.example.com/pb?sub={sub_id}&e={event}&sum={payout}"
        onChange={(e) => {
          setUrl(e.target.value);
          setState('idle');
        }}
      />
      <div className="profile__postback-btns">
        <button type="button" className="btn-accent" disabled={state === 'busy' || !url.trim()} onClick={() => void save(url.trim())}>
          {state === 'saved' ? 'Сохранено' : 'Сохранить'}
        </button>
        {p.postbackUrl ? (
          <button type="button" className="link-btn" onClick={() => void save(null).then(() => setUrl(''))}>
            Выключить
          </button>
        ) : null}
      </div>
      {state === 'error' ? <p className="error-text">Нужен https-адрес</p> : null}
    </div>
  );
}

function Toggle({ on, label, onChange }: { on: boolean; label: string; onChange: (v: boolean) => void }) {
  return (
    <label className="settings__row">
      <span>{label}</span>
      <input
        type="checkbox"
        role="switch"
        className="switch"
        checked={on}
        onChange={(e) => {
          haptic('select');
          onChange(e.target.checked);
        }}
      />
    </label>
  );
}

function Account({ me, onMe, onTwin }: { me: Me; onMe: (me: Me) => void; onTwin: () => void }) {
  const notify = me.settings?.notify ?? { ready: true, trends: true, promo: true };
  const [confirm, setConfirm] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [twinPhotos, setTwinPhotos] = useState<number | null>(null);
  const [report, setReport] = useState(false);

  useEffect(() => {
    api
      .twin()
      .then((t) => setTwinPhotos(t.okPhotos))
      .catch(() => {});
  }, []);

  const set = async (k: keyof typeof notify, v: boolean) => {
    onMe({ ...me, settings: { notify: { ...notify, [k]: v } } });
    try {
      onMe(await api.updateSettings({ [k]: v }));
    } catch {
      onMe(me);
      setMsg('Не сохранилось — проверь интернет.');
    }
  };

  const forget = async () => {
    try {
      const r = await api.forget();
      hapticResult('success');
      setMsg(r.deleted ? `Удалили: фото, двойника и результаты (${r.deleted} файлов). Баланс на месте.` : 'Удалять было нечего — у нас нет твоих фото.');
      setTwinPhotos(0);
    } catch (err) {
      setMsg(err instanceof ApiError ? 'Не получилось удалить. Напиши в поддержку.' : 'Нет связи. Попробуй ещё раз.');
    }
    setConfirm(false);
  };

  return (
    <div className="settings">
      {SUPPORT_URL ? (
        <button type="button" className="settings__support" onClick={() => openTelegram(SUPPORT_URL)}>
          <span className="settings__support-ic">
            <Headset />
          </span>
          <span className="trend__face-text">
            <span className="settings__support-title">Поддержка</span>
            <span className="trend__face-sub">Живой человек ответит за 10 минут</span>
          </span>
          <ChevronDim />
        </button>
      ) : null}

      <div className="settings__h">Уведомления в боте</div>
      <div className="settings__g">
        <Toggle on={notify.ready} label="Генерация готова" onChange={(v) => void set('ready', v)} />
        <div className="settings__d" />
        <Toggle on={notify.trends} label="Новые тренды" onChange={(v) => void set('trends', v)} />
        <div className="settings__d" />
        <Toggle on={notify.promo} label="Скидки и бонусы" onChange={(v) => void set('promo', v)} />
      </div>

      <div className="settings__h">Приватность</div>
      <div className="settings__g">
        <div className="settings__row">
          Хранить историю<span className="settings__v">30 дней</span>
        </div>
        <div className="settings__d" />
        <button type="button" className="settings__row" onClick={onTwin}>
          Мой двойник и фото<span className="settings__v">{twinPhotos === null ? '' : `${twinPhotos} фото`}</span>
        </button>
        <div className="settings__d" />
        <button type="button" className="settings__row" onClick={() => setReport(true)}>
          Моё фото использовали без согласия
        </button>
        <div className="settings__d" />
        {confirm ? (
          <div className="settings__confirm">
            <span>Удалим двойника, все фото, селфи и результаты генераций. Баланс и партнёрка останутся. Это навсегда.</span>
            <div className="twin__confirm-btns">
              <button type="button" className="btn-glass" onClick={() => setConfirm(false)}>
                Отмена
              </button>
              <button type="button" className="twin__delete" onClick={() => void forget()}>
                Удалить
              </button>
            </div>
          </div>
        ) : (
          <button type="button" className="settings__row settings__row--danger" onClick={() => setConfirm(true)}>
            Удалить все мои данные
          </button>
        )}
      </div>
      {msg ? (
        <p className="profile__msg" role="status">
          {msg}
        </p>
      ) : null}

      <div className="settings__h">Прочее</div>
      <div className="settings__g">
        <div className="settings__row">
          Язык<span className="settings__v">Русский</span>
        </div>
        <div className="settings__d" />
        <button type="button" className="settings__row" onClick={() => openLegal('terms')}>
          Пользовательское соглашение
        </button>
        <div className="settings__d" />
        <button type="button" className="settings__row" onClick={() => openLegal('privacy')}>
          Конфиденциальность и фото
        </button>
      </div>

      <p className="settings__ver">Trendator 1.0 · ID {me.id}</p>
      {report ? <ReportSheet reason="my_face" onClose={() => setReport(false)} /> : null}
    </div>
  );
}
